import { AlertTriangle, Home, RotateCcw } from 'lucide-react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { Button } from '@/components/ui/button';

/**
 * Friendly fallback rendered when a route's render / loader throws.
 * Mounted as the `errorElement` on every route — replaces React
 * Router's default "Unexpected Application Error / Hey developer 👋"
 * dev page so the user (cashier, owner, accountant) sees something
 * branded instead of a stack trace.
 *
 * Two layers in the app:
 *   - This one (route-level) catches an uncaught error in any handler
 *     under that route and shows the friendly page.
 *   - `SectionErrorBoundary` (per-widget) keeps the rest of a page
 *     alive when ONE section blows up.
 *
 * See memory `feedback_react-router-error-boundary.md`.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const { title, description } = describe(error);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-peach-100 text-peach-500">
          <AlertTriangle className="h-7 w-7" strokeWidth={1.75} />
        </div>
        <h1 className="text-xl font-semibold text-ink-800">{title}</h1>
        <p className="mt-2 text-sm text-ink-500">{description}</p>

        <div className="mt-6 flex items-center justify-center gap-2">
          <Button variant="secondary" onClick={() => navigate(0)}>
            <RotateCcw className="h-4 w-4" strokeWidth={1.75} />
            Réessayer
          </Button>
          <Button onClick={() => navigate('/')}>
            <Home className="h-4 w-4" strokeWidth={1.75} />
            Retour à l’accueil
          </Button>
        </div>

        {import.meta.env.DEV && (
          <details className="mt-6 rounded-sm bg-surface-sunken p-3 text-left">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-ink-500">
              Détail technique (dev only)
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-ink-800">
              {devDetail(error)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

function describe(error: unknown): { title: string; description: string } {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: 'Page introuvable',
        description: 'Cette adresse n’existe pas. Retournez à l’accueil pour continuer.',
      };
    }
    if (error.status === 403) {
      return {
        title: 'Accès refusé',
        description: 'Vous n’avez pas les permissions pour cette page.',
      };
    }
    if (error.status >= 500) {
      return {
        title: 'Erreur serveur',
        description: 'Notre serveur a renvoyé une erreur. Réessayez dans un instant.',
      };
    }
    return {
      title: 'Erreur inattendue',
      description: error.statusText || `Code ${error.status}`,
    };
  }

  return {
    title: 'Une erreur est survenue',
    description:
      'Cette section ne peut pas être affichée pour le moment. Réessayez, ou retournez à l’accueil.',
  };
}

function devDetail(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}\n\n${error.stack ?? ''}`;
  }
  if (isRouteErrorResponse(error)) {
    return `${error.status} ${error.statusText}\n\n${JSON.stringify(error.data, null, 2)}`;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}
